/*
  bogos.sync.js (safe v8.2 - supports thumbnail-swatch labels)
  - Fix bouncing by overriding max-height via CSS only (no JS style writes)
  - Keep BOGOS in control of dropdown selection (no capture-phase blocking)
  - Radio opens/closes tiers (display-based)
  - Main Color change updates ALL BOGOS dropdowns by clicking matching BOGOS options
  - Adds explicit support for: label.thumbnail-swatch (value read from .sr-only or linked input)
  - Re-applies after BOGOS re-renders
*/

(() => {
  const ROOT_SELECTOR =
    '.bogos-volume-discount-widget, #bogos-volume-discount-view, .bogos-integration-page-builder-volume-discount';

  const TIER_ITEM_SELECTOR = '.bogos-volume-discount_item-container';
  const RADIO_SELECTOR = 'input.bogos-volume-discount-tier-input[type="radio"]';
  const COLLAPSE_SELECTOR = '.bogos-volume-discount-variants-collapse';

  const SELECT_VARIANT_BTN = '.bogos-volume-discount-item-select-button';

  const BOGOS_DROPDOWN_SELECTOR = '.bogos-vl-select-variant-dropdown';
  const BOGOS_OPTIONS_SELECTOR = '.bogos-vl-select-variant-options';
  const BOGOS_OPTION_SELECTOR = '.bogos-vl-select-variant-option';

  // Your theme swatch labels
  const THEME_SWATCH_LABEL_SELECTOR = 'label.thumbnail-swatch';

  const STYLE_ID = 'bogos-tier-lock-css-v8';

  // ----------------------------
  // CSS: override max-height (CSS-only to avoid bouncing loops)
  // ----------------------------
  function injectCSS() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      /* Hide the "Select variant" button */
      ${SELECT_VARIANT_BTN} { display: none !important; }

      /* We control accordion via classes */
      .bogos-js-collapsed { display: none !important; }
      .bogos-js-expanded  { display: block !important; }

      /* Prevent animation bounce */
      ${COLLAPSE_SELECTOR} { transition: none !important; }

      /* Stop BOGOS max-height animation from affecting layout */
      ${COLLAPSE_SELECTOR}.bogos-js-expanded  { max-height: none !important; height: auto !important; overflow: visible !important; }
      ${COLLAPSE_SELECTOR}.bogos-js-collapsed { max-height: 0 !important; height: 0 !important; overflow: hidden !important; }
    `;
    document.head.appendChild(style);
  }

  // ----------------------------
  // Tier open/close helpers
  // ----------------------------
  function closestTierContainer(el) {
    return el?.closest?.(TIER_ITEM_SELECTOR) || null;
  }

  function getCollapsePanel(tierContainer) {
    return tierContainer?.querySelector?.(COLLAPSE_SELECTOR) || null;
  }

  function forceClosed(panel) {
    if (!panel) return;
    panel.classList.remove('bogos-js-expanded');
    panel.classList.add('bogos-js-collapsed');
  }

  function forceOpen(panel) {
    if (!panel) return;
    panel.classList.remove('bogos-js-collapsed');
    panel.classList.add('bogos-js-expanded');
  }

  function setOnlySelectedTierOpen() {
    const tierContainers = document.querySelectorAll(TIER_ITEM_SELECTOR);
    if (!tierContainers.length) return;

    const checked = document.querySelector(`${RADIO_SELECTOR}:checked`);
    const activeTier = closestTierContainer(checked);

    tierContainers.forEach((tier) => {
      const panel = getCollapsePanel(tier);
      if (!panel) return;
      if (tier === activeTier) forceOpen(panel);
      else forceClosed(panel);
    });
  }

  // ----------------------------
  // Main product Color detection (enhanced for thumbnail-swatch)
  // ----------------------------
  function getSelectedColorValue() {
    // Shopify common patterns
    const optColorRadio =
      document.querySelector('input[type="radio"][name="options[Color]"]:checked') ||
      document.querySelector('input[type="radio"][name*="options[Color]"]:checked');
    if (optColorRadio) return (optColorRadio.value || '').trim();

    const optColorSelect =
      document.querySelector('select[name="options[Color]"]') ||
      document.querySelector('select[name*="options[Color]"]');
    if (optColorSelect?.value) return optColorSelect.value.trim();

    const colorRadio =
      document.querySelector('input[type="radio"][name="Color"]:checked') ||
      document.querySelector('input[type="radio"][name*="Color"]:checked');
    if (colorRadio) return (colorRadio.value || '').trim();

    const colorSelect =
      document.querySelector('select[name="Color"]') ||
      document.querySelector('select[name*="Color"]');
    if (colorSelect?.value) return colorSelect.value.trim();

    // Theme swatches: try checked input that label.thumbnail-swatch points to
    const checkedSwatchInput =
      document.querySelector('input[type="radio"][id^="option-value-"]:checked') ||
      document.querySelector('input[type="radio"][id*="main-option"]:checked');
    if (checkedSwatchInput?.value) return checkedSwatchInput.value.trim();

    // Swatch containers some themes use
    const selectedSwatch =
      document.querySelector('[data-option-name="Color"][aria-checked="true"]') ||
      document.querySelector('[data-option-name="Color"].is-selected') ||
      document.querySelector('[data-option="Color"][aria-checked="true"]');
    if (selectedSwatch) {
      const v =
        selectedSwatch.getAttribute('data-value') ||
        selectedSwatch.getAttribute('aria-label') ||
        selectedSwatch.getAttribute('title') ||
        selectedSwatch.textContent;
      return (v || '').trim();
    }

    // "Color: X" fallback
    const textNodes = document.querySelectorAll('p, span, div, label, dt, dd');
    for (const el of textNodes) {
      const t = (el.textContent || '').replace(/\s+/g, ' ').trim();
      const m = t.match(/\bColor\s*:\s*(.+)\b/i);
      if (m?.[1]) {
        const val = m[1].trim();
        if (val && val.length < 80) return val;
      }
    }

    return '';
  }

  // ----------------------------
  // Sync BOGOS dropdowns (click the option so BOGOS updates its own state)
  // ----------------------------
  function clickBogosOptionByText(dropdownEl, targetText) {
    if (!dropdownEl || !targetText) return false;

    const optionsWrap = dropdownEl.querySelector(BOGOS_OPTIONS_SELECTOR);
    if (!optionsWrap) return false;

    const opts = Array.from(optionsWrap.querySelectorAll(BOGOS_OPTION_SELECTOR));
    if (!opts.length) return false;

    const normTarget = targetText.trim().toLowerCase();

    // exact match first
    let match = opts.find((o) => (o.textContent || '').trim().toLowerCase() === normTarget);

    // fallback: contains match (handles tiny formatting differences)
    if (!match) {
      match = opts.find((o) => (o.textContent || '').trim().toLowerCase().includes(normTarget));
    }
    if (!match) return false;

    match.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
    match.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
    match.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    return true;
  }

  function syncAllBogosDropdownsToColor(forcedColor = '') {
    const color = (forcedColor || getSelectedColorValue() || '').trim();
    if (!color) return;

    const root = document.querySelector(ROOT_SELECTOR) || document;
    const dropdowns = root.querySelectorAll(BOGOS_DROPDOWN_SELECTOR);
    dropdowns.forEach((dd) => clickBogosOptionByText(dd, color));
  }

  // ----------------------------
  // Scheduling helper (handles async DOM updates after swatch click)
  // ----------------------------
  function scheduleSync(forcedColor = '') {
    // run now + a few retries after theme/BOGOS updates
    syncAllBogosDropdownsToColor(forcedColor);
    setTimeout(() => syncAllBogosDropdownsToColor(forcedColor), 80);
    setTimeout(() => syncAllBogosDropdownsToColor(forcedColor), 180);
    setTimeout(() => syncAllBogosDropdownsToColor(forcedColor), 350);
  }

  // ----------------------------
  // Listeners
  // ----------------------------
  function bindTierRadioListener() {
    document.addEventListener(
      'change',
      (e) => {
        const t = e.target;
        if (t?.matches?.(RADIO_SELECTOR)) {
          setOnlySelectedTierOpen();
          scheduleSync(); // use detected color
        }
      },
      true
    );
  }

  function bindMainVariantListeners() {
    // Generic change listener (works for selects/radios that actually fire change)
    document.addEventListener(
      'change',
      (e) => {
        const el = e.target;
        if (!el) return;

        const name = (el.getAttribute && el.getAttribute('name')) || '';
        const looksLikeColor =
          name.includes('Color') ||
          name.includes('options[Color]') ||
          // theme radio ids used by swatches
          (el.id && (el.id.startsWith('option-value-') || el.id.includes('main-option'))) ||
          el.closest?.('[data-option-name="Color"]') ||
          el.closest?.('[data-option="Color"]');

        if (looksLikeColor) scheduleSync();
      },
      true
    );

    // ✅ Explicit support for label.thumbnail-swatch clicks (your case)
    document.addEventListener(
      'click',
      (e) => {
        const label = e.target?.closest?.(THEME_SWATCH_LABEL_SELECTOR);
        if (!label) return;

        // Grab color name from sr-only first (your markup)
        const sr = label.querySelector('.sr-only');
        let chosen = (sr?.textContent || '').trim();

        // Fallback: grab from the linked input by "for="
        if (!chosen && label.htmlFor) {
          const input = document.getElementById(label.htmlFor);
          chosen = (input?.value || input?.getAttribute?.('value') || '').trim();
        }

        // Do NOT rely on immediate state; theme updates after click
        // So schedule sync with the chosen text (best), else detect later.
        scheduleSync(chosen);
      },
      true
    );

    // Theme / app custom events (harmless if not used)
    ['variant:change', 'variant:changed', 'product:variant-change', 'shopify:variant:change'].forEach((evt) => {
      document.addEventListener(evt, () => scheduleSync());
    });
  }

  // ----------------------------
  // Init + re-render handling
  // ----------------------------
  function init() {
    injectCSS();
    setOnlySelectedTierOpen();
    scheduleSync();
  }

  init();
  setTimeout(init, 300);

  // Re-init if BOGOS re-renders its widget
  const obs = new MutationObserver((muts) => {
    for (const m of muts) {
      for (const node of m.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (
          node.matches?.('.bogos-volume-discount_item-container') ||
          node.querySelector?.('.bogos-volume-discount_item-container') ||
          node.matches?.(ROOT_SELECTOR) ||
          node.querySelector?.(ROOT_SELECTOR)
        ) {
          init();
          return;
        }
      }
    }
  });
  obs.observe(document.body, { childList: true, subtree: true });

  bindTierRadioListener();
  bindMainVariantListeners();
})();




